/**
 * Wildebeast cross-sell / upsell
 * ---------------------------------------------------------------------------
 * Loads recommendations from Shopify's Recommendations API after first paint,
 * renders cards, and runs the "add all" bundle bar.
 *
 * Deliberately fetches the .json endpoint rather than a rendered section:
 * it is a fraction of the payload and lets us build cards that match this
 * section's own markup instead of inheriting another section's.
 */

const ROOT = () => window.Shopify?.routes?.root || '/';

function money(cents) {
  const amount = cents / 100;
  const currency = window.Shopify?.currency?.active || 'INR';
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount}`;
  }
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function sized(url, width) {
  if (!url) return '';
  return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
}

class WbCrossSell extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-xs-grid]');
    this.bundleBar = this.querySelector('[data-xs-bundle]');
    this.useBundle = this.dataset.bundle === 'true';
    this.products = [];

    if (this.dataset.productId) this.loadRecommendations();
    else this.afterRender(); // manual mode — cards are already server-rendered
  }

  async loadRecommendations() {
    const url = new URL(`${ROOT()}recommendations/products.json`, window.location.origin);
    url.searchParams.set('product_id', this.dataset.productId);
    url.searchParams.set('limit', this.dataset.limit || '4');
    url.searchParams.set('intent', this.dataset.intent || 'related');

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Recommendations failed: ${response.status}`);
      const data = await response.json();
      const products = data.products || [];

      // An empty rail under a heading looks broken. Remove the section instead.
      if (!products.length) {
        this.hidden = true;
        return;
      }

      this.products = products;
      if (this.grid) this.grid.innerHTML = products.map((p) => this.card(p)).join('');
      this.afterRender();
    } catch {
      this.hidden = true;
    }
  }

  card(product) {
    const variant = product.variants?.find((v) => v.available) || product.variants?.[0];
    const onSale = product.compare_at_price > product.price;
    const image = product.featured_image || product.images?.[0];

    return `
      <li class="wb-xs__card">
        <a class="wb-xs__media" href="${escapeHtml(product.url)}">
          ${
            image
              ? `<img src="${escapeHtml(sized(image, 600))}" alt="${escapeHtml(product.title)}"
                   width="400" height="500" loading="lazy" decoding="async">`
              : ''
          }
          ${!product.available ? '<span class="wb-xs__badge">Sold out</span>' : ''}
          ${
            this.useBundle && product.available && variant
              ? `<label class="wb-xs__select">
                   <input type="checkbox" data-xs-select value="${variant.id}"
                          data-price="${product.price}"
                          aria-label="Include ${escapeHtml(product.title)} in bundle">
                   <span class="wb-xs__select-mark"></span>
                 </label>`
              : ''
          }
        </a>
        <div class="wb-xs__info">
          <a class="wb-xs__title" href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a>
          <p class="wb-xs__price">
            ${money(product.price)}
            ${onSale ? `<s>${money(product.compare_at_price)}</s>` : ''}
          </p>
          ${
            product.available && variant
              ? `<button type="button" class="wb-xs__add" data-xs-add="${variant.id}">Add</button>`
              : ''
          }
        </div>
      </li>`;
  }

  afterRender() {
    this.wireQuickAdd();
    if (this.useBundle) this.wireBundle();
  }

  wireQuickAdd() {
    this.querySelectorAll('[data-xs-add]').forEach((button) => {
      button.addEventListener('click', async () => {
        const id = button.dataset.xsAdd;
        if (!id) return;

        const original = button.textContent;
        button.disabled = true;
        button.textContent = 'Adding…';

        const ok = await addToCart([{ id: Number(id), quantity: 1 }]);
        button.textContent = ok ? 'Added ✓' : 'Try again';

        setTimeout(() => {
          button.textContent = original;
          button.disabled = false;
        }, 1800);
      });
    });
  }

  wireBundle() {
    this.checkboxes = Array.from(this.querySelectorAll('[data-xs-select]'));
    if (!this.checkboxes.length || !this.bundleBar) return;

    this.countEl = this.querySelector('[data-xs-bundle-count]');
    this.totalEl = this.querySelector('[data-xs-bundle-total]');
    this.addAllBtn = this.querySelector('[data-xs-bundle-add]');

    // Pre-select everything: opt-out converts better than opt-in, and the
    // shopper can see exactly what they're about to buy.
    this.checkboxes.forEach((box) => {
      box.checked = true;
      box.addEventListener('change', () => this.updateBundle());
      // The checkbox sits inside the card's <a>, so stop it navigating.
      box.closest('.wb-xs__select')?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.target !== box) box.checked = !box.checked;
        this.updateBundle();
      });
    });

    this.addAllBtn?.addEventListener('click', () => this.addAll());
    this.bundleBar.hidden = false;
    this.updateBundle();
  }

  updateBundle() {
    const selected = this.checkboxes.filter((b) => b.checked);
    const total = selected.reduce((sum, b) => sum + Number(b.dataset.price || 0), 0);

    if (this.countEl) this.countEl.textContent = String(selected.length);
    if (this.totalEl) this.totalEl.textContent = selected.length ? money(total) : '—';
    if (this.addAllBtn) this.addAllBtn.disabled = selected.length === 0;
  }

  async addAll() {
    const items = this.checkboxes
      .filter((b) => b.checked)
      .map((b) => ({ id: Number(b.value), quantity: 1 }));

    if (!items.length) return;

    const original = this.addAllBtn.textContent;
    this.addAllBtn.disabled = true;
    this.addAllBtn.textContent = 'Adding…';

    const ok = await addToCart(items);
    this.addAllBtn.textContent = ok ? 'Added ✓' : 'Try again';

    setTimeout(() => {
      this.addAllBtn.textContent = original;
      this.updateBundle();
    }, 2000);
  }
}

/**
 * Single add-to-cart path for every button in this section, so cart events
 * are dispatched consistently and the drawer/badge always refresh.
 */
async function addToCart(items) {
  try {
    const response = await fetch(`${ROOT()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    if (!response.ok) return false;

    ['cart:updated', 'cart:refresh', 'cart:change'].forEach((name) =>
      document.dispatchEvent(new CustomEvent(name))
    );
    return true;
  } catch {
    return false;
  }
}

if (!customElements.get('wb-cross-sell')) customElements.define('wb-cross-sell', WbCrossSell);
